import {
  Box,
  Card,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import React from 'react';
import { AccessibleTextArea } from '../../../../../components/inputs/AccessibleTextArea';
import { StyledSectionHeading } from '../../../../../components/inputs/InputLabel';
import { LocalizedRichTextField } from '../../../../../components/inputs/LocalizedRichTextField';
import { StyledQuestionMark } from '../../../../../components/QuestionMark';
import { BrandingResponse } from '../../../../../types/Branding.types';

type InternalInformationSectionProps = {
  branding?: BrandingResponse;
  notes: string;
  hide_agreement: boolean;
  disabled?: boolean;
  description?: Record<string, string>;
  locales?: string[];
  onDescriptionChange?: (value: Record<string, string>) => void;
  onNotesChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onDisabledChange: (checked: boolean) => void;
};

export const InternalInformationSection = ({
  branding,
  notes,
  hide_agreement,
  disabled = false,
  description,
  locales,
  onDescriptionChange,
  onNotesChange,
  onDisabledChange,
}: InternalInformationSectionProps) => {
  const showDescription = !branding?.show_agreement_description;

  return (
    <>
      <Box sx={{ marginTop: { xs: '14px', md: '0px' } }}>
        <StyledSectionHeading>Internal information</StyledSectionHeading>
      </Box>
      <Card
        variant="secondary"
        sx={{
          '&:hover': {
            backgroundColor: 'var(--color-greyish-blue)',
          },
        }}
      >
        {showDescription &&
          description !== undefined &&
          locales &&
          onDescriptionChange && (
            <Box mb={14}>
              <LocalizedRichTextField
                id="description"
                label={
                  <>
                    Agreement description
                    <Tooltip
                      title="A short internal description for this agreement"
                      placement="top"
                    >
                      <IconButton
                        size="small"
                        sx={{ marginLeft: '3px' }}
                        aria-label="A short internal description for this agreement"
                      >
                        <StyledQuestionMark fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                }
                locales={locales}
                value={description}
                onChange={onDescriptionChange}
                disabled={disabled}
                warnEmpty
              />
            </Box>
          )}
        <Box>
          <AccessibleTextArea
            id="notes"
            label={
              <>
                Notes
                <Tooltip title="Additional internal notes" placement="top">
                  <IconButton
                    size="small"
                    sx={{ marginLeft: '3px' }}
                    aria-label="Additional internal notes"
                  >
                    <StyledQuestionMark fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            }
            name="notes"
            placeholder="Notes"
            onChange={onNotesChange}
            value={notes}
            style={{ maxWidth: '100%' }}
            disabled={disabled}
            className="bg-white"
            rows={4}
          />
        </Box>
      </Card>
      <FormControlLabel
        control={
          <Checkbox
            checked={hide_agreement}
            onChange={(e) => onDisabledChange(e.target.checked)}
            disabled={disabled}
          />
        }
        label="Do not show this agreement in public search"
      />
    </>
  );
};
